import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SidebarNavItemProps {
  to: string;
  label: string;
  icon: React.ElementType;
  collapsed: boolean;
  disabled?: boolean;
}

export default function SidebarNavItem({
  to,
  label,
  icon: Icon,
  collapsed,
  disabled,
}: SidebarNavItemProps) {
  const { pathname } = useLocation();
  const isActive = !disabled && (pathname === to || (to !== "/" && pathname.startsWith(to)));

  const item = disabled ? (
    <span
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium cursor-not-allowed opacity-40",
        collapsed ? "justify-center px-0" : "",
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
      {!collapsed && <span>{label}</span>}
    </span>
  ) : (
    <Link
      to={to}
      aria-current={isActive ? "page" : undefined}
      // Collapsed, the link is an icon and nothing else, and the tooltip below
      // is not an accessible name: it opens on hover and focus, and assistive
      // technology reads the link before either happens. So the sweep found
      // twelve links with no name at all on every route measured with the
      // sidebar collapsed (BEN-141 QF11, `a11y.unlabeled-control`). Expanded,
      // the label is the link's own text and a duplicate aria-label would only
      // override it with the same words.
      aria-label={collapsed ? label : undefined}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
        collapsed ? "justify-center px-0" : "",
        isActive
          ? "bg-sidebar-primary text-sidebar-primary-foreground"
          : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={isActive ? 2 : 1.75} />
      {!collapsed && <span>{label}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return item;
}
